import React, { useEffect, useState } from 'react';
import { Form, Modal, Spin } from 'antd';
import { observer } from 'mobx-react-lite';
import { z } from 'zod';
import { useForm } from 'antd/es/form/Form';
import { stepFinished } from '../../prequalNav';
import { usePrequal } from '../../stores/prequalStore';
import {
  pushToDataLayer,
  formatEpochDate,
  sendCustomEventToIterable,
  formatIterablePayload,
} from '../../utils';
import PreQualFunnelLayout from '../PreQualFunnelLayout';
import { NavigationRow } from '../components/NavigationRow/NavigationRow';
import SectionHeader from '../components/SectionHeader/SectionHeader';
import TextInput from '../components/TextInput/TextInput';
import PhoneInput from '../components/PhoneInput/PhoneInput';
import FooterCard from '../components/FooterCard/FooterCard';
import { Address, PHONE_NUMBER_REGEX, Prequal } from '../../schema';
import { emailValidator } from '../../validators/emailValidator';
import PrequalAddressAutocomplete from '../components/PrequalAddressAutocomplete/PrequalAddressAutocomplete';
import styles from './AboutYouStep.module.scss';
import { completePrequalFunnelV2 } from '../../utils/prequalHelper';
import { LEAD_SOURCE } from '../../constants';

const formItemLayout = {
  labelCol: { span: 0 },
  wrapperCol: { span: 24 },
};

const AboutYouStep = observer(() => {
  const prequalStore = usePrequal();
  const { prequal } = prequalStore;
  const [canContinue, setCanContinue] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [address, setAddress] = useState(prequal.address);
  const [form] = useForm();

  const update = async (values: any) => {
    const castedPrequal = prequal as Prequal;
    prequalStore.setAboutYou(values, address);
    const source = prequal?.source ? LEAD_SOURCE.get(prequal.source) ?? 'Other' : 'Other';
    sendCustomEventToIterable({
      email: prequal.email as string,
      eventName: 'PreQualStarted',
      eventData: formatIterablePayload(castedPrequal, {
        applicationStatus: prequal.prequalApplicationStatus,
        startedAt: prequal.createdAt ? formatEpochDate(prequal.createdAt) : undefined,
        source,
        ...(castedPrequal.imtId === 'NONBDP' && { boatImtId: null }),
      }),
    });
    setSubmitting(true);
    await completePrequalFunnelV2();
    setSubmitting(false);
    pushToDataLayer('link_click', {
      action_type: 'click',
      action_label: 'About you - Continue',
    });
    stepFinished();
  };

  const checkValues = (changed: any, values: any) => {
    const { firstName, lastName, email } = values;

    if (!firstName || !lastName || !email) {
      setCanContinue(false);
    } else {
      setCanContinue(true);
    }
  };

  const validatePhone = async (_: any, value: any) => {
    const isValid = PHONE_NUMBER_REGEX.test(value);
    const isEmpty = (value?.length ?? 0) === 0;

    if (!isValid && !isEmpty) {
      setCanContinue(false);
      throw new Error('Phone number is not valid');
    }
  };

  const validator = async (rule: any, value: any) => {
    if (!value) {
      setCanContinue(false);
      if (rule.field === 'completeAddress') {
        throw new Error('Please enter your full home address so we can show accurate loan options.');
      }
      throw new Error('This field is required');
    }
    if (rule.field === 'email') {
      try {
        const emailSchema = z.string().email().refine(emailValidator);
        emailSchema.parse(value);
      } catch {
        setCanContinue(false);
        throw new Error('Email is not valid');
      }
    }
  };

  const formatAddress = (selectedAddress: Address) => {
    let fullAddress = selectedAddress.address1;
    if (selectedAddress.city) {
      fullAddress += `, ${selectedAddress.city}`;
    }
    if (selectedAddress.state) {
      fullAddress += `, ${selectedAddress.state}`;
    }
    if (selectedAddress.zip) {
      fullAddress += ` ${selectedAddress.zip}`;
    }

    return fullAddress;
  };

  useEffect(() => {
    pushToDataLayer('pageview', {
      detailed_page_name: 'about you',
      site_section: 'loan application',
    });
  }, []);

  useEffect(() => {
    const values = prequal as Prequal;
    form.setFieldsValue({
      completeAddress: prequal.address ? formatAddress(prequal.address) : '',
    });
    checkValues(null, values);
  }, [prequal, form]);

  const header = {
    title: 'About you',
    subtitle: 'Your info helps us match you with the right loan.',
    content: 'The more accurate your information is, the better we can match you with personalized loan offers.',
  };

  return (
    <PreQualFunnelLayout>
      <Modal visible={submitting} footer={null} closable={false}>
        <p>Checking offers for you...</p>
        <Spin size="large" style={{ marginTop: '2rem', display: 'block' }} />
      </Modal>
      <SectionHeader header={header} />
      <Form
        {...formItemLayout}
        form={form}
        initialValues={prequal}
        onFinish={update}
        onValuesChange={checkValues}
      >
        <div className={styles.aboutYouContainer}>
          <div className={styles.aboutYouTextItem}>
            <TextInput labelText="First Name" name="firstName" validator={validator} />
          </div>
          <div className={styles.aboutYouTextItem}>
            <TextInput labelText="Last Name" name="lastName" validator={validator} />
          </div>
          <div className={styles.aboutYouTextItem}>
            <TextInput labelText="Email" name="email" validator={validator} />
          </div>
          <div className={styles.aboutYouTextItem}>
            <PhoneInput labelText="Phone" name="phone" validator={validatePhone} />
          </div>
          <div className={styles.aboutYouTextItemFull}>
            <PrequalAddressAutocomplete
              name="completeAddress"
              required
              validator={validator}
              onSelect={(selectedAddress: Address) => {
                const valuesArray = [
                  {
                    name: 'address1',
                    value: selectedAddress.address1 || '',
                  },
                  { name: 'unitNumber', value: selectedAddress.unitNumber },
                  { name: 'city', value: selectedAddress.city },
                  { name: 'county', value: selectedAddress.county },
                  { name: 'country', value: selectedAddress.country },
                  { name: 'state', value: selectedAddress.state },
                  { name: 'zip', value: selectedAddress.zip },
                ];
                const fieldsObject = {};
                valuesArray.forEach((item) =>
                  Object.assign(fieldsObject, { [item.name]: item.value })
                );
                form.setFieldsValue({
                  completeAddress: formatAddress(selectedAddress),
                });
                form.setFields(valuesArray);
                setAddress(selectedAddress);
              }}
            />
          </div>
        </div>

        <NavigationRow canContinue={canContinue} form={form} />
      </Form>

      <FooterCard showFirstItem />
    </PreQualFunnelLayout>
  );
});

export default AboutYouStep;
